import { useCallback, useEffect, useState } from "react";
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from "@/components/ui/Card";
import { Switch } from "@/components/ui/Switch";
import { Alert, AlertDescription } from "@/components/ui/Alert";

interface EmailCategory {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
}

interface EmailPreferences {
  global_enabled: boolean;
  categories: Record<string, EmailCategory>;
}

export default function EmailPreferencesSection() {
  const [preferences, setPreferences] = useState<EmailPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedField, setSavedField] = useState<string | null>(null);

  // Load preferences on component mount
  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch("/web_api/email/preferences", {
        credentials: "include", // Include cookies for authentication
      });

      if (!response.ok) {
        let errorMessage = `Failed to load preferences (${response.status})`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          // Ignore JSON parse errors
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setPreferences(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load email preferences. Please try again.",
      );
      console.error("Error loading preferences:", err);
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = useCallback(
    async (
      updates: {
        global_enabled?: boolean;
        categories?: Record<string, boolean>;
      },
      fieldName: string,
    ) => {
      if (!preferences) return;

      try {
        setSaving(true);
        setError(null);
        setSavedField(null);

        const response = await fetch("/web_api/email/preferences", {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include", // Include cookies for authentication
          body: JSON.stringify(updates),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to update preferences");
        }

        // Update local state
        setPreferences((prev) => {
          if (!prev) return prev;
          const newPrefs = { ...prev };

          if (updates.global_enabled !== undefined) {
            newPrefs.global_enabled = updates.global_enabled;
          }

          if (updates.categories) {
            Object.entries(updates.categories).forEach(([category, enabled]) => {
              if (newPrefs.categories[category]) {
                newPrefs.categories[category] = {
                  ...newPrefs.categories[category],
                  enabled,
                };
              }
            });
          }

          return newPrefs;
        });

        setSavedField(fieldName);
        setTimeout(() => setSavedField(null), 1500);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to update preferences. Please try again.",
        );
        console.error("Error updating preferences:", err);
      } finally {
        setSaving(false);
      }
    },
    [preferences],
  );

  const handleGlobalToggle = useCallback(
    (enabled: boolean) => {
      updatePreference({ global_enabled: enabled }, "global");
    },
    [updatePreference],
  );

  const handleCategoryToggle = useCallback(
    (categoryId: string, enabled: boolean) => {
      updatePreference({ categories: { [categoryId]: enabled } }, categoryId);
    },
    [updatePreference],
  );

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Email Preferences</CardTitle>
          <CardDescription>Manage your email communication preferences</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-12 bg-muted rounded"></div>
            <div className="space-y-3">
              <div className="h-10 bg-muted rounded"></div>
              <div className="h-10 bg-muted rounded"></div>
              <div className="h-10 bg-muted rounded"></div>
              <div className="h-10 bg-muted rounded"></div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!preferences) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Email Preferences</CardTitle>
          <CardDescription>Manage your email communication preferences</CardDescription>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertDescription>{error || "Failed to load email preferences"}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const categoryList = Object.entries(preferences.categories).map(([id, category]) => ({
    ...category,
    id,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Email Preferences</CardTitle>
        <CardDescription>
          Choose which updates and information you&apos;d like to receive
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Master toggle */}
        <div className="flex items-center justify-between gap-4">
          <label htmlFor="email-global" className="flex flex-col flex-1 cursor-pointer">
            <span className="text-sm font-medium leading-none">
              Stay informed about Uptime Monitor
            </span>
            <span className="text-sm text-muted-foreground mt-1">
              Get product updates, helpful tips, and important announcements. You&apos;ll always
              receive critical account and monitoring alerts regardless of this setting.
            </span>
          </label>
          <div className="flex items-center gap-3">
            <svg
              className={`w-5 h-5 text-success transition-opacity duration-200 ${
                savedField === "global" ? "opacity-100" : "opacity-0"
              }`}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M5 13l4 4L19 7"></path>
            </svg>
            <Switch
              id="email-global"
              checked={preferences.global_enabled}
              onCheckedChange={handleGlobalToggle}
              disabled={saving}
              className="mt-0.5"
            />
          </div>
        </div>

        {/* Category toggles */}
        <div className="space-y-3 pt-4 border-t">
          {categoryList.map((category) => (
            <div key={category.id} className="flex items-center justify-between gap-4">
              <label
                htmlFor={`email-category-${category.id}`}
                className="flex flex-col flex-1 cursor-pointer"
              >
                <span className="text-sm font-medium leading-none">{category.name}</span>
                <span className="text-sm text-muted-foreground mt-1">{category.description}</span>
              </label>
              <div className="flex items-center gap-3">
                <svg
                  className={`w-5 h-5 text-success transition-opacity duration-200 ${
                    savedField === category.id ? "opacity-100" : "opacity-0"
                  }`}
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M5 13l4 4L19 7"></path>
                </svg>
                <Switch
                  id={`email-category-${category.id}`}
                  checked={preferences.global_enabled && category.enabled}
                  onCheckedChange={(enabled) => handleCategoryToggle(category.id, enabled)}
                  disabled={saving || !preferences.global_enabled}
                  className="mt-0.5"
                />
              </div>
            </div>
          ))}
        </div>

        <div className="pt-4 border-t">
          <p className="text-sm text-muted-foreground">
            You can unsubscribe from individual emails at any time using the link in each message.
            Critical notifications about your monitors and account security will always be sent
            regardless of these preferences.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
